import React, { useState, useEffect, useCallback } from 'react';
import { KeyRound, CheckCircle, AlertTriangle, Lock, Zap, Save, Trash2, RefreshCw } from 'lucide-react';
import { aiSetupAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const GREEN = '#1B5C3A';
const GOLD = '#F5A623';
const RED = '#dc2626';
const CREAM = '#FAF9F6';
const CHAR = '#1a1a1a';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';
const IN = { padding: '10px 12px', borderRadius: 9, border: `1px solid ${BORDER}`, fontSize: 14, fontFamily: 'var(--font-sans)', boxSizing: 'border-box', width: '100%' };

const PROVIDERS = [
  { id: 'anthropic', name: 'Anthropic (Claude)', hint: 'Recommended. Key starts with sk-ant-…', placeholder: 'sk-ant-…', model: 'claude-haiku-4-5-20251001' },
  { id: 'openai', name: 'OpenAI (GPT)', hint: 'Key starts with sk-…', placeholder: 'sk-…', model: 'gpt-4o-mini' },
  { id: 'gemini', name: 'Google (Gemini)', hint: 'Google AI Studio key', placeholder: 'AIza…', model: 'gemini-1.5-flash' },
];

export default function AiSetup() {
  useAuth();
  const [status, setStatus] = useState(null);
  const [provider, setProvider] = useState('anthropic');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [test, setTest] = useState(null);

  const load = useCallback(() => aiSetupAPI.status()
    .then(r => { const d = r.data.data || r.data; setStatus(d); if (d.provider) setProvider(d.provider); })
    .catch(e => setErr(e.response?.data?.message || 'Failed to load')), []);
  useEffect(() => { load(); }, [load]);

  const chosen = PROVIDERS.find(p => p.id === provider) || PROVIDERS[0];

  const save = () => {
    setBusy('save'); setMsg(''); setErr(''); setTest(null);
    aiSetupAPI.save({ provider, apiKey, model: model || undefined })
      .then(r => { setMsg(r.data.message || 'Saved.'); setApiKey(''); const d = r.data.data; if (d) setStatus(d); })
      .catch(e => setErr(e.response?.data?.message || 'Save failed'))
      .finally(() => setBusy(''));
  };

  const runTest = () => {
    setBusy('test'); setMsg(''); setErr(''); setTest(null);
    aiSetupAPI.test()
      .then(r => setTest(r.data.data || { ok: true }))
      .catch(e => setErr(e.response?.data?.message || 'Test failed'))
      .finally(() => setBusy(''));
  };

  const clear = () => {
    if (!window.confirm('Remove the saved AI key? AI features will stop until a new key is set.')) return;
    setBusy('clear'); setMsg(''); setErr(''); setTest(null);
    aiSetupAPI.clear()
      .then(r => { setMsg(r.data.message || 'Cleared.'); const d = r.data.data; if (d) setStatus(d); })
      .catch(e => setErr(e.response?.data?.message || 'Clear failed'))
      .finally(() => setBusy(''));
  };

  if (!status && err) return <div style={{ padding: 24 }}><div className="card" style={{ padding: 24, borderLeft: `4px solid ${RED}`, color: RED }}>{err}</div></div>;
  if (!status) return <div style={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center', color: MUTED }}>Loading…</div>;

  const locked = status.lockedByEnv;

  return (
    <div style={{ padding: '32px 40px', background: CREAM, minHeight: '100vh' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 800, color: CHAR, margin: 0, letterSpacing: '-0.02em' }}>
          <KeyRound size={26} style={{ verticalAlign: -4, marginRight: 8, color: GREEN }} />AI Setup
        </h1>
        <p style={{ color: MUTED, fontSize: 15, marginTop: 6, fontWeight: 500, maxWidth: 820, lineHeight: 1.55 }}>
          The single AI key powering Catalog Attributes, Product Discovery and Vendor Discovery. Set it once here — no server access needed.
        </p>
      </div>

      {/* Current status */}
      <div className="card" style={{ padding: 20, marginBottom: 18, display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {status.configured
            ? <span style={{ display: 'flex', alignItems: 'center', gap: 7, color: GREEN, fontWeight: 800, fontSize: 15 }}><CheckCircle size={20} />Connected</span>
            : <span style={{ display: 'flex', alignItems: 'center', gap: 7, color: RED, fontWeight: 800, fontSize: 15 }}><AlertTriangle size={20} />Not configured</span>}
        </div>
        {status.configured && (
          <>
            <div><div style={cap}>Provider</div><div style={val}>{status.provider}</div></div>
            <div><div style={cap}>Key</div><div style={{ ...val, fontFamily: 'monospace' }}>{status.maskedKey}</div></div>
            <div><div style={cap}>Model</div><div style={val}>{status.model}</div></div>
            <div><div style={cap}>Source</div><div style={val}>{status.source === 'env' ? '.env file' : 'Admin (saved)'}</div></div>
          </>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: `1px solid ${BORDER}`, background: '#fff', color: MUTED, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}><RefreshCw size={14} />Refresh</button>
        {status.configured && (
          <button onClick={runTest} disabled={!!busy} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 9, border: 'none', background: GREEN, color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
            <Zap size={15} />{busy === 'test' ? 'Testing…' : 'Test key'}
          </button>
        )}
      </div>

      {test && <div style={okBox}><CheckCircle size={16} /> Key works — {test.provider} / {test.model} replied “{test.reply}”.</div>}
      {msg && <div style={okBox}>{msg}</div>}
      {err && <div style={{ ...okBox, background: 'rgba(220,38,38,0.07)', color: RED }}><AlertTriangle size={16} />{err}</div>}

      {locked ? (
        <div className="card" style={{ padding: 20, display: 'flex', gap: 12, alignItems: 'flex-start', borderLeft: `4px solid ${GOLD}` }}>
          <Lock size={20} style={{ color: GOLD, flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 14, color: CHAR, lineHeight: 1.55 }}>
            <b>Managed in <code>backend/.env</code>.</b> A key set in the environment file takes precedence and can’t be changed from here.
            To manage the key from this screen instead, remove the AI key line from <code>backend/.env</code> and restart the server.
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 24, maxWidth: 640 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 800, color: CHAR }}>{status.configured ? 'Replace key' : 'Add AI key'}</h3>

          <label style={lbl}>Provider</label>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            {PROVIDERS.map(p => (
              <button key={p.id} onClick={() => setProvider(p.id)} type="button" style={{
                padding: '10px 16px', borderRadius: 10, cursor: 'pointer', fontSize: 13.5, fontWeight: 700, textAlign: 'left',
                border: `1.5px solid ${provider === p.id ? GREEN : BORDER}`, background: provider === p.id ? 'rgba(27,92,58,0.06)' : '#fff', color: provider === p.id ? GREEN : MUTED,
              }}>{p.name}</button>
            ))}
          </div>

          <label style={lbl}>API key</label>
          <input style={IN} type="password" autoComplete="off" placeholder={chosen.placeholder} value={apiKey} onChange={e => setApiKey(e.target.value)} />
          <div style={{ fontSize: 12.5, color: MUTED, margin: '6px 2px 16px' }}>{chosen.hint}</div>

          <label style={lbl}>Model <span style={{ color: MUTED, fontWeight: 500 }}>(optional)</span></label>
          <input style={IN} placeholder={`default: ${chosen.model}`} value={model} onChange={e => setModel(e.target.value)} />
          <div style={{ fontSize: 12.5, color: MUTED, margin: '6px 2px 20px' }}>Leave blank to use the recommended default for this provider.</div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={save} disabled={busy === 'save' || !apiKey} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '11px 22px', borderRadius: 10, border: 'none', background: apiKey ? GREEN : '#cbd5d1', color: '#fff', fontSize: 14, fontWeight: 700, cursor: apiKey ? 'pointer' : 'not-allowed' }}>
              <Save size={16} />{busy === 'save' ? 'Saving…' : 'Save key'}
            </button>
            {status.configured && status.source !== 'env' && (
              <button onClick={clear} disabled={!!busy} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '11px 18px', borderRadius: 10, border: `1px solid ${RED}55`, background: '#fff', color: RED, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                <Trash2 size={15} />Remove
              </button>
            )}
          </div>
          <p style={{ fontSize: 12.5, color: MUTED, marginTop: 16, lineHeight: 1.5 }}>
            After saving, go to <b>Catalog Attributes</b> and hit <b>Generate for all</b> to build the buyer spec forms for every category, sub-category and brand.
          </p>
        </div>
      )}
    </div>
  );
}

const cap = { fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 700 };
const val = { fontSize: 15, fontWeight: 700, color: CHAR };
const lbl = { display: 'block', fontSize: 13, fontWeight: 700, color: CHAR, marginBottom: 7 };
const okBox = { display: 'flex', alignItems: 'center', gap: 8, padding: '11px 16px', marginBottom: 14, borderRadius: 10, background: 'rgba(27,92,58,0.08)', color: GREEN, fontSize: 13.5, fontWeight: 600 };
